import type { LoginResponse, SessionUser } from '@qms360/shared';

/**
 * API client.
 *
 * Rule 1 / ADR-0013: the base URL comes from the environment. Nothing here is a
 * literal, because this same build runs against localhost in dev and against a
 * path-prefixed origin behind Algihaz's nginx in production.
 *
 * ADR-0015: the access token is short-lived and carries identity only. This client
 * refreshes it transparently on a 401 and retries once — the alternative is
 * bouncing the user to the login screen every fifteen minutes for no security gain.
 */

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

const ACCESS_TOKEN_KEY = 'qms360.accessToken';
const REFRESH_TOKEN_KEY = 'qms360.refreshToken';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * What to tell a person about a refused request: the server's message and,
 * when it lists them, every reason at once — fixing a record should not take a
 * retry per problem (pass 9: App 3 showed only the headline).
 */
export function describeError(caught: unknown, fallback: string): string {
  if (!(caught instanceof ApiError)) return fallback;
  const details = caught.details as { blockers?: unknown } | undefined;
  const blockers = Array.isArray(details?.blockers)
    ? details.blockers
        .map((blocker) => (typeof blocker === 'string' ? blocker : ((blocker as { message?: string })?.message ?? '')))
        .filter(Boolean)
    : [];
  return blockers.length ? `${caught.message} ${blockers.join(' ')}` : caught.message;
}

/**
 * The last signed-in user, kept on the device so the app can start with no
 * signal (DATA-1, spec §10). A display cache only: every request is still
 * re-authorized by the server (ADR-0015), and it is cleared whenever the server
 * says the sign-in is over.
 */
const SESSION_USER_KEY = 'qms360.sessionUser';
export const sessionCache = {
  read<T>(): T | null {
    try {
      const raw = localStorage.getItem(SESSION_USER_KEY);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  },
  write(user: unknown): void {
    try {
      localStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));
    } catch {
      // A full or unavailable store only costs the offline start.
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(SESSION_USER_KEY);
    } catch {
      // Nothing to clear.
    }
  },
};

export const tokenStore = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  set(access: string, refresh: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

async function parseError(response: Response): Promise<never> {
  let code: string | undefined;
  let message = response.statusText;
  let details: unknown;

  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string; details?: unknown } };
    code = body.error?.code;
    message = body.error?.message ?? message;
    details = body.error?.details;
  } catch {
    // A non-JSON error body is rare but not worth failing over.
  }

  throw new ApiError(response.status, message, code, details);
}

async function attemptRefresh(): Promise<boolean> {
  const refreshToken = tokenStore.refresh;
  if (!refreshToken) return false;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // No signal. The sign-in may be perfectly good; keep it (DATA-1).
    return false;
  }

  // Only the server saying the sign-in is no good ends it. A gateway error or a
  // server that is down is not a verdict on the person, and used to sign a
  // field engineer out for losing signal.
  if (response.status === 400 || response.status === 401 || response.status === 403) {
    tokenStore.clear();
    sessionCache.clear();
    return false;
  }
  if (!response.ok) return false;

  const body = (await response.json()) as LoginResponse;
  tokenStore.set(body.accessToken, body.refreshToken);
  return true;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  { retryOnUnauthorized = true }: { retryOnUnauthorized?: boolean } = {},
): Promise<T> {
  const token = tokenStore.access;

  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401 && retryOnUnauthorized) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      return apiFetch<T>(path, init, { retryOnUnauthorized: false });
    }
  }

  if (!response.ok) await parseError(response);
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

/**
 * Fetch a binary response — a stored file.
 *
 * Separate from `apiFetch` because that one parses JSON, and because an
 * authenticated image cannot be an `<img src>`: the access token travels in a
 * header, which an image element cannot set. ADR-0013 rules out the usual way
 * round that — a signed storage URL — so the bytes come through the API here and
 * become an object URL on the other side.
 */
export async function apiFetchBlob(
  path: string,
  { retryOnUnauthorized = true }: { retryOnUnauthorized?: boolean } = {},
): Promise<Blob> {
  const token = tokenStore.access;

  const response = await fetch(`${BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (response.status === 401 && retryOnUnauthorized) {
    const refreshed = await attemptRefresh();
    if (refreshed) return apiFetchBlob(path, { retryOnUnauthorized: false });
  }

  if (!response.ok) await parseError(response);
  return response.blob();
}

// --- Typed endpoints -------------------------------------------------------

export interface LoginResult {
  accessToken: string;
  refreshToken: string;
  user: SessionUser;
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const result = await apiFetch<LoginResult>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  tokenStore.set(result.accessToken, result.refreshToken);
  return result;
}

export async function logout(): Promise<void> {
  try {
    await apiFetch<void>('/auth/logout', { method: 'POST' });
  } finally {
    // Clear locally even if the call failed — the server session may already be
    // revoked, which is exactly when the call fails.
    tokenStore.clear();
    sessionCache.clear();
  }
}

export interface MeResponse {
  user: SessionUser;
  platformRole: string | null;
  workspaceRoles: { app: string; roleKey: string | null }[];
  authSource: 'local' | 'container';
  /** Master data (Rule 1), so the form can state the rule before a refusal does. */
  passwordPolicy: { minLength: number };
  /** Per app, the permissions this person holds somewhere in their scope (pass 9). */
  capabilities?: Record<string, string[]>;
}

export const fetchMe = () => apiFetch<MeResponse>('/auth/me');

/** Choose the interface language (pass 12). */
export const setMyLocale = (locale: 'en' | 'ar') =>
  apiFetch<{ locale: 'en' | 'ar' }>('/auth/me/locale', { method: 'PUT', body: JSON.stringify({ locale }) });

export const changePassword = (currentPassword: string, newPassword: string) =>
  apiFetch<{ revokedSessions: number }>('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });

export interface AppSummary {
  key: 'qaqc' | 'lesson-learned' | 'audit';
  label: string;
  shortLabel: string;
  hasAccess: boolean;
}

export const fetchApps = () =>
  apiFetch<{ apps: AppSummary[]; executive?: { hasAccess: boolean } }>('/apps');

export interface AppHome {
  app: { key: string; label: string; shortLabel: string };
  hasWorkspaceAccess: boolean;
  modules: { key: string; label: string; permission: string }[];
  emptyStateReason: string | null;
}

export const fetchAppHome = (app: string) => apiFetch<AppHome>(`/apps/${app}/home`);

export interface AppSettings {
  app: { key: string; label: string };
  sections: { key: string; label: string; description: string; permission: string }[];
}

export const fetchAppSettings = (app: string) => apiFetch<AppSettings>(`/apps/${app}/settings`);

// ---------------------------------------------------------------------------
// App 1 — QA/QC & Document Governance (spec §9)
// ---------------------------------------------------------------------------

export type RecordStatus = 'draft' | 'submitted' | 'sent_back' | 'approved';

export interface ReportPeriod {
  id: string;
  projectId: string;
  businessUnitId: string | null;
  periodStart: string;
  status: RecordStatus;
  submittedBy: string | null;
  submittedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  sentBackRemarks: string | null;
  /** Derived. Null means not applicable — render an em dash, never 0. */
  pqiValue: string | null;
  pqiVariance: string | null;
  pqiExplanation: string | null;
  pqiInputs: {
    isProvisional?: boolean;
    appliedWeights?: Record<string, number>;
    categories?: { category: string; active: boolean; value: number | null }[];
  };
  createdBy: string | null;
}

export interface MetricEntry {
  id: string;
  category: 'external_ncr' | 'internal_ncr' | 'rfi' | 'rmi';
  issuedCount: number;
  closedCount: number;
  ageing0To15: number;
  ageing15To45: number;
  ageingOver45: number;
  closureRate: string | null;
  closureRateVariance: string | null;
  closureRateExplanation: string | null;
}

export interface MaterialInspection {
  id: string;
  mirnTotal: number;
  osdOverage: number;
  osdShortage: number;
  osdDamage: number;
  statusApproved: number;
  statusOnHold: number;
  statusRejected: number;
  statusHazardous: number;
  statusHandleWithCare: number;
}

export interface QtbtEntry {
  id: string;
  sessionCount: number;
  attendeeCount: number;
  totalDurationMinutes: number;
  averageAttendance: string | null;
  averageAttendanceExplanation: string | null;
}

export interface QualityBrief {
  id: string;
  narrative: string;
  aiSuggestionId: string | null;
  aiDisposition: string | null;
}

export interface ReportPeriodDetail {
  period: ReportPeriod;
  metrics: MetricEntry[];
  materialInspection: MaterialInspection | null;
  qtbt: QtbtEntry | null;
  customerSatisfaction: CsatResponse[];
  brief: QualityBrief | null;
}

export const fetchPeriods = () => apiFetch<{ periods: ReportPeriod[] }>('/qaqc/periods');

export const fetchPeriod = (id: string) => apiFetch<ReportPeriodDetail>(`/qaqc/periods/${id}`);

export const createPeriod = (projectId: string, periodStart: string) =>
  apiFetch<{ period: ReportPeriod }>('/qaqc/periods', {
    method: 'POST',
    body: JSON.stringify({ projectId, periodStart }),
  });

export const saveMetrics = (periodId: string, body: Omit<MetricEntry, 'id' | 'closureRate' | 'closureRateVariance' | 'closureRateExplanation'>) =>
  apiFetch<{ entry: MetricEntry }>(`/qaqc/periods/${periodId}/metrics`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const saveMaterialInspection = (periodId: string, body: Omit<MaterialInspection, 'id'>) =>
  apiFetch<{ entry: MaterialInspection }>(`/qaqc/periods/${periodId}/material-inspection`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const saveQtbt = (periodId: string, body: { sessionCount: number; attendeeCount: number; totalDurationMinutes: number }) =>
  apiFetch<{ entry: QtbtEntry }>(`/qaqc/periods/${periodId}/qtbt`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const submitPeriod = (id: string) =>
  apiFetch<{ period: ReportPeriod }>(`/qaqc/periods/${id}/submit`, { method: 'POST' });

export const approvePeriod = (id: string) =>
  apiFetch<{ period: ReportPeriod }>(`/qaqc/periods/${id}/approve`, { method: 'POST' });

export const sendBackPeriod = (id: string, remarks: string) =>
  apiFetch<{ period: ReportPeriod }>(`/qaqc/periods/${id}/send-back`, {
    method: 'POST',
    body: JSON.stringify({ remarks }),
  });

export const fetchApprovals = () => apiFetch<{ pending: ReportPeriod[] }>('/qaqc/approvals');

export interface AiDraft {
  suggestionId: string | null;
  text: string | null;
  timedOut: boolean;
  unavailableReason: string | null;
}

export const requestAiDraft = (periodId: string) =>
  apiFetch<AiDraft>(`/qaqc/periods/${periodId}/brief/ai-draft`, { method: 'POST' });

export const saveBrief = (
  periodId: string,
  body: { narrative: string; aiSuggestionId?: string | null; aiDisposition?: string | null },
) =>
  apiFetch<{ brief: QualityBrief }>(`/qaqc/periods/${periodId}/brief`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** Export streams through the backend, never a raw storage URL (ADR-0013). */
export async function downloadPeriodExport(periodId: string): Promise<void> {
  const token = tokenStore.access;
  const response = await fetch(`${BASE_URL}/qaqc/periods/${periodId}/export.xlsx`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) await parseError(response);

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = match?.[1] ?? 'qms360-report.xlsx';
  link.click();
  URL.revokeObjectURL(url);
}

// --- Dashboard and Document Governance -------------------------------------

export interface DashboardRow {
  periodId: string;
  periodStart: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  projectGroup: string | null;
  businessUnitName: string | null;
  status: RecordStatus;
  pqiValue: number | null;
  pqiVariance: number | null;
  pqiExplanation: string | null;
  isProvisional: boolean;
  issued: number;
  closed: number;
  closureRate: number | null;
  closureRateExplanation: string;
  ageingOver45: number;
  belowTarget: boolean | null;
}

export interface Dashboard {
  filters: { category?: string; appliedCategories: string[] };
  target: number | null;
  rows: DashboardRow[];
  summary: {
    projectCount: number;
    periodCount: number;
    approvedCount: number;
    issued: number;
    closed: number;
    closureRate: number | null;
    closureRateExplanation: string;
    ageingOver45: number;
    averagePqi: number | null;
    belowTargetPeriods: number;
  };
}

export const fetchDashboard = (params: Record<string, string> = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== ''),
  ).toString();
  return apiFetch<Dashboard>(`/qaqc/dashboard${query ? `?${query}` : ''}`);
};

export interface DocumentGovernanceEntry {
  id: string;
  projectId: string;
  disciplineId: string | null;
  logDate: string;
  submittalsTotal: number;
  drawingsTotal: number;
  statusApproved: number;
  statusResubmit: number;
  statusRejected: number;
  statusUnderReview: number;
  reviewDaysTotal: number;
  reviewedCount: number;
  averageReviewDays: string | null;
  averageReviewDaysExplanation: string | null;
  correspondenceIncoming: number;
  correspondenceOutgoing: number;
  status: RecordStatus;
  sentBackRemarks: string | null;
}

export const fetchDocumentGovernance = () =>
  apiFetch<{ entries: DocumentGovernanceEntry[] }>('/qaqc/document-governance');

export const saveDocumentGovernance = (body: Record<string, unknown>) =>
  apiFetch<{ entry: DocumentGovernanceEntry }>('/qaqc/document-governance', {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const submitDocumentGovernance = (id: string) =>
  apiFetch<{ entry: DocumentGovernanceEntry }>(`/qaqc/document-governance/${id}/submit`, {
    method: 'POST',
  });

export const sendBackDocumentGovernance = (id: string, remarks: string) =>
  apiFetch<{ entry: DocumentGovernanceEntry }>(`/qaqc/document-governance/${id}/send-back`, {
    method: 'POST',
    body: JSON.stringify({ remarks }),
  });

export const approveDocumentGovernance = (id: string) =>
  apiFetch<{ entry: DocumentGovernanceEntry }>(`/qaqc/document-governance/${id}/approve`, {
    method: 'POST',
  });

export interface FortnightRollUp {
  range: { fromDate: string; toDate: string };
  entryCount: number;
  approvedEntryCount: number;
  totals: Record<string, number>;
  averageReviewDays: number | null;
  averageReviewDaysExplanation: string;
  pendingDaysByEntity: Record<string, number>;
}

export const fetchFortnightRollUp = (fromDate: string, toDate: string) =>
  apiFetch<FortnightRollUp>(
    `/qaqc/document-governance/fortnight?fromDate=${fromDate}&toDate=${toDate}`,
  );

// --- Customer satisfaction --------------------------------------------------

export interface CsatResponse {
  id: string;
  respondentName: string | null;
  ratingQuality: number | null;
  ratingTimeliness: number | null;
  ratingCommunication: number | null;
  ratingResponsiveness: number | null;
  ratingDocumentation: number | null;
  ratingOverall: number | null;
  feedback: string | null;
}

export const addCsatResponse = (periodId: string, body: Record<string, unknown>) =>
  apiFetch<{ entry: CsatResponse }>(`/qaqc/periods/${periodId}/customer-satisfaction`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const archiveCsatResponse = (periodId: string, responseId: string) =>
  apiFetch<void>(`/qaqc/periods/${periodId}/customer-satisfaction/${responseId}`, {
    method: 'DELETE',
  });

/** What a workbook would change, or did (pass 8: bulk Excel import). */
export interface WorkbookImportReport {
  applied: boolean;
  changes: { section: string; item: string; field: string; current: number | null; incoming: number }[];
  problems: { sheet: string; row: number | null; message: string }[];
  notImported: string[];
}

/** Preview (the default) or apply this month's exported workbook, filled in. */
export const importPeriodWorkbook = (periodId: string, data: string, dryRun: boolean) =>
  apiFetch<{ report: WorkbookImportReport }>(`/qaqc/periods/${periodId}/import`, {
    method: 'POST',
    body: JSON.stringify({ data, dryRun }),
  });
